import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ErrorParser } from '@app/classes';
import { RqRemittanceFrequencies } from '@app/constants/rq-remittance-frequencies';
import { FeatureFlag } from '@app/enums';
import { Model } from '@app/models/core/base.model';
import { AuthorizationDocument } from '@app/models/payroll/self-onboarding-document.model';
import {
    AuthorizationDocumentType,
    PAYROLL_AUTHORIZATION_DOCUMENTS,
} from '@app/modules/payroll/types/self-onboarding-document-type.type';
import { NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { BaseForm } from '@forms/base.form';
import { CompanyInformation } from '@payroll/interfaces';
import { CompanyInformationService, SelfOnboardingDocumentsService } from '@payroll/services';

type Mode = 'view' | 'edit';

type ParsableError = Parameters<typeof ErrorParser.parse>[0];

@Component({
    selector: 'app-self-onboarding-company-information-form',
    templateUrl: './company-information.template.html',
    styleUrls: ['./company-information.style.scss'],
})
export class CompanyInformationForm extends BaseForm implements OnInit {
    @Output() loading = new EventEmitter<boolean>();
    @Output() editing = new EventEmitter<boolean>();

    companyInformation?: CompanyInformation;
    openBankingInfoDialog = false;
    authorizationDocumentsHaveBeenUploaded = false;
    hasQcPayrollFeature = false;
    rqRemittanceFrequencyOptions = Object.entries(RqRemittanceFrequencies).map(([value, label]) => ({ label, value }));

    private _mode: Mode = 'view';
    private _loading = true;
    private originalCompanyInformation?: CompanyInformation;

    constructor(
        private companyInformationService: CompanyInformationService,
        private notify: NotifyService,
        private featureService: FeatureService,
        private selfOnboardingDocumentsService: SelfOnboardingDocumentsService
    ) {
        super();
    }

    get mode(): Mode {
        return this._mode;
    }
    set mode(val: Mode) {
        this._mode = val;
        this.editing.emit(val === 'edit');
    }

    get isLoading(): boolean {
        return this._loading;
    }
    set isLoading(val: boolean) {
        this._loading = val;
        this.loading.emit(val);
    }

    async ngOnInit(): Promise<void> {
        this.featureService.has(FeatureFlag.qcPayroll).then((hasQcPayrollFeature) => {
            this.hasQcPayrollFeature = hasQcPayrollFeature;
        });

        try {
            const companyInformation = await this.companyInformationService.getCompanyInformation();
            this.companyInformation = companyInformation;
            this.originalCompanyInformation = { ...companyInformation };
            await this.getAuthorizationDocuments();
        } catch (error) {
            this.notify.error(ErrorParser.parse(error as ParsableError));
        }

        this.isLoading = false;
    }

    editCompanyInformation(): void {
        this.mode = 'edit';
    }

    cancelEdit(): void {
        this.mode = 'view';
        if (!this.companyInformation || !this.originalCompanyInformation) return;
        this.companyInformation = { ...this.originalCompanyInformation };
        Object.values(this.companyInformation).forEach((value) => value instanceof Model && value.revert());
    }

    async getAuthorizationDocuments(): Promise<void> {
        const authorizationDocuments: Record<AuthorizationDocumentType, AuthorizationDocument> =
            await this.selfOnboardingDocumentsService.getDocuments(PAYROLL_AUTHORIZATION_DOCUMENTS);
        let padAgreementUploaded = false;
        let roeAgreementUploaded = false;
        Object.entries(authorizationDocuments).some(([key, value]) => {
            if (key === 'PadAgreement') {
                padAgreementUploaded = value.state === 'new' || value.state === 'rejected' ? false : true;
            }
            if (key === 'ROEAuthorization') {
                roeAgreementUploaded = value.state === 'new' || value.state === 'rejected' ? false : true;
            }
        });
        if (padAgreementUploaded || roeAgreementUploaded) {
            this.authorizationDocumentsHaveBeenUploaded = true;
        }
    }

    async updateCompanyInformation(): Promise<void> {
        if (!this.companyInformation || !this.originalCompanyInformation) return;

        // Call `submit` to run validation
        this.submit();

        if (this.form.invalid || this.form.pristine) {
            return;
        }

        this.isLoading = true;

        try {
            await this.companyInformationService.updateCompanyInformation(this.companyInformation);
            this.originalCompanyInformation = { ...this.companyInformation };
            this.mode = 'view';
            this.notify.success('payroll.setup.companyInformationSuccessfullyUpdated');
        } catch (error) {
            this.notify.error(
                ErrorParser.parse(error as ParsableError) || 'payroll.setup.weWereUnableToUpdateYourInformation'
            );
        }

        this.isLoading = false;
    }

    toggleBankingInfoDialog(): void {
        this.openBankingInfoDialog = !this.openBankingInfoDialog;
    }
}
